import { Card, CardContent, CardFooter } from "@loot-os/ui/components/card";
import { Link } from "@tanstack/react-router";

import type { PromoCard, PromoCardsContent } from "../../pages/home/content/promoCards.js";
import { ClassicActionLink } from "../ClassicActionLink.js";

/** The classic promo cards: one row under the hero, shared by however many are shown. */
export function ClassicPromoCards({ cards }: PromoCardsContent) {
  return (
    // One equal column per card shown, so a hidden card (sign-up once signed in, battles when
    // they are off) leaves no gap: the rest share the row. Stacked below md.
    <div className="grid gap-4 md:auto-cols-fr md:grid-flow-col md:gap-2.5">
      {cards.map((card) => (
        <ClassicPromoCard key={card.key} card={card} />
      ))}
    </div>
  );
}

/** Actions in a CardFooter, or the whole card as the link — never both, so no anchor nests. */
function ClassicPromoCard({ card }: { card: PromoCard }) {
  const body = (
    <>
      <CardContent className="flex flex-1 flex-col gap-1">
        {card.eyebrow ? (
          <>
            <p className="text-2xl leading-none font-bold tracking-tight text-muted-foreground">
              {card.eyebrow}
            </p>
            <p className="text-4xl leading-snug font-bold tracking-tight text-foreground">
              {card.title}
            </p>
          </>
        ) : (
          <p className="text-2xl leading-none font-bold tracking-tight text-foreground">
            {card.title}
          </p>
        )}
      </CardContent>
      {card.kind === "actions" && (
        <CardFooter>
          <div className="flex w-full flex-col gap-2">
            {card.actions.map((action) => (
              <ClassicActionLink key={action.label} action={action} size="sm" />
            ))}
          </div>
        </CardFooter>
      )}
    </>
  );

  if (card.kind === "actions") {
    return <Card className="justify-between overflow-hidden md:min-h-56">{body}</Card>;
  }

  return (
    <Link
      to={card.destination.to}
      className="group rounded-xl outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      {/* ring, not border: Card's hairline is `ring-1`, so a border-color hover would show nothing. */}
      <Card className="h-full justify-between overflow-hidden transition-colors group-hover:ring-primary/50 md:min-h-56">
        {body}
      </Card>
    </Link>
  );
}
